import { Box, TextField } from '@mui/material';
import { AppGlobalContext } from 'contexts/GlobalContext';
import { forwardRef, useContext, useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { useBoundStore } from 'useStore';
import { useShallow } from 'zustand/react/shallow';
import usePricingData from '../../Queries/pricingQuery';
import useMeliFreeShipping from '../../Queries/MeliFreeShippingMutation';
import { getCountryCode } from '../../utils/country';

const NumericFormatCustom = forwardRef(function NumericFormatCustom(
	props,
	ref
) {
	const { onChange, ...other } = props;

	return (
		<NumericFormat
			{...other}
			getInputRef={ref}
			onValueChange={(values) => {
				onChange({
					target: {
						name: props.name,
						value: values.value,
					},
				});
			}}
			thousandSeparator
			valueIsNumericString
			prefix="$"
			style={{ textAlign: 'center' }}
		/>
	);
});

export default function EditNumberInput(props) {
	const { id, value, field, hasFocus, row } = props;
	const pricingQuery = usePricingData();
	const editUsdCost = useBoundStore((state) => state.editUsdCost);
	const editPvpDisbyte = useBoundStore((state) => state.editPvpDisbyte);
	const applyMeliFreeShipping = useBoundStore(
		(state) => state.applyMeliFreeShipping
	);
	const pricingEditedRow = useBoundStore(
		useShallow((state) => state.pricingEditedRows[id])
	);
	const addPricingRowSelection = useBoundStore(
		(state) => state.addPricingRowSelection
	);
	const removePricingRowSelection = useBoundStore(
		(state) => state.removePricingRowSelection
	);

	const { mainCountry } = useContext(AppGlobalContext);
	const countryName = mainCountry?.pais;
	const dolar = pricingQuery?.data?.dolarPrice;

	const currentRow = pricingEditedRow || row;

	const trueValue = currentRow[field] ?? value;

	const meliFsMutation = useMeliFreeShipping();
	const fsFetchTimerRef = useRef(null);
	const lastFsKeyRef = useRef(null);

	// Cuando se edita el PVP proyectado de un item MELI, consultamos a MELI el costo
	// real (FS o operating cost según freeshipping). El override se rutea al campo
	// correcto dentro de calculateProyectedData (FS si freeshipping=true, fixrate si false).
	// Se engancha solo en la celda de pvpdisbyte_proyectado para no duplicar la llamada
	// con la celda de usdcost_proyectado.
	useEffect(() => {
		if (field !== 'pvpdisbyte_proyectado') return;
		const pvpProy = pricingEditedRow?.pvpdisbyte_proyectado;
		if (!pvpProy || pvpProy <= 0) return;
		if (pricingEditedRow?.site !== 'MELI') return;
		if (
			pricingEditedRow.shippingmode === 'me1' &&
			!pricingEditedRow.freeshipping
		)
			return;

		const sellerId =
			pricingEditedRow.seller_id ??
			pricingEditedRow.sellerid ??
			pricingEditedRow.user_id ??
			pricingEditedRow.userid ??
			null;
		const itemId = pricingEditedRow.itemid;
		if (!itemId) return;

		// siteId derivado del país (MLA/MLM/MLC/MLB). Permite que el backend
		// resuelva el sellerId/access_token cuando la fila no lo trae.
		const siteId = pricingEditedRow.siteid ?? getCountryCode(countryName);

		const params = {
			sellerId: sellerId ? String(sellerId) : undefined,
			siteId,
			itemId,
			itemPrice: pvpProy,
			listingTypeId: pricingEditedRow.listingtypeid,
			shippingMode: pricingEditedRow.shippingmode,
			logisticType: pricingEditedRow.logistictype,
			condition: pricingEditedRow.condition ?? 'new',
		};

		const key = JSON.stringify(params);
		if (lastFsKeyRef.current === key) return;

		if (fsFetchTimerRef.current) clearTimeout(fsFetchTimerRef.current);
		fsFetchTimerRef.current = setTimeout(() => {
			lastFsKeyRef.current = key;
			meliFsMutation.mutate(params, {
				onSuccess: (data) => {
					const listCost = data?.listCost ?? data?.ListCost;
					if (listCost && listCost > 0) {
						applyMeliFreeShipping(id, listCost, dolar, countryName);
					}
				},
			});
		}, 500);

		return () => {
			if (fsFetchTimerRef.current) clearTimeout(fsFetchTimerRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		pricingEditedRow?.pvpdisbyte_proyectado,
		pricingEditedRow?.usdcost_proyectado,
		pricingEditedRow?.listingtypeid,
		pricingEditedRow?.shippingmode,
		pricingEditedRow?.logistictype,
		pricingEditedRow?.itemid,
		countryName,
	]);

	const handleValueChange = (event) => {
		const newValue = event.target.value; // The new value entered by the user

		const newNumber = Number.parseFloat(newValue);

		if (field === 'usdcost_proyectado') {
			editUsdCost(currentRow, newNumber, dolar, countryName);
		}

		if (field === 'pvpdisbyte_proyectado') {
			editPvpDisbyte(currentRow, newNumber, dolar, countryName);
		}

		if (Number.parseFloat(newValue) > 0) {
			addPricingRowSelection(id);
		}

		if (shouldRemovePricingRowSelection(field, newValue, currentRow)) {
			removePricingRowSelection(id);
		}
	};

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				width: '100%',
				height: '100%',
				backgroundColor: pricingEditedRow ? '#e4f2fe' : 'white',
				padding: 0,
			}}
		>
			<TextField
				label=""
				placeholder="$0.00"
				type="text"
				value={trueValue}
				onChange={handleValueChange}
				name={field}
				id={field}
				sx={{
					textAlign: 'center',
				}}
				InputProps={{
					inputComponent: NumericFormatCustom,
					disableUnderline: true,
					style: { textAlign: 'center' },
				}}
				inputRef={(input) => input && hasFocus && input.focus()}
				variant="standard"
			/>
		</Box>
	);
}

function shouldRemovePricingRowSelection(field, newValue, currentRow) {
	return (
		(field === 'usdcost_proyectado' &&
			(!newValue || Number.parseFloat(newValue) <= 0) &&
			(!currentRow.pvpdisbyte_proyectado ||
				currentRow.pvpdisbyte_proyectado <= 0)) ||
		(field === 'pvpdisbyte_proyectado' &&
			(!newValue || Number.parseFloat(newValue) <= 0) &&
			(!currentRow.usdcost_proyectado || currentRow.usdcost_proyectado <= 0))
	);
}
